package com.eladfinish.bible.contest.ui.layout

import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp

/**
 * Provides an adaptive number of grid columns based on the available max width.
 *
 * This is Compose Multiplatform friendly (no Android-only APIs) and reacts to:
 * - device rotation (portrait/landscape)
 * - window resizing (desktop)
 * - split view / multitasking (tablet)
 *
 * @param narrowColumns Columns count for narrow screens.
 * @param wideColumns Columns count for wide screens.
 * @param wideScreenThreshold Width threshold from which [wideColumns] will be used.
 * @param content Receives the calculated columns count.
 */
@Composable
fun WithAdaptiveGridColumns(
    modifier: Modifier = Modifier,
    narrowColumns: Int,
    wideColumns: Int,
    wideScreenThreshold: Dp = 700.dp,
    content: @Composable (columnsCount: Int) -> Unit,
) {
    BoxWithConstraints(modifier = modifier) {
        val columnsCount = if (maxWidth >= wideScreenThreshold) wideColumns else narrowColumns
        content(columnsCount)
    }
}


@Preview(name = "Narrow (<= 700dp)", widthDp = 360, heightDp = 800)
@Composable
private fun WithAdaptiveGridColumnsPreview_Narrow() {
    WithAdaptiveGridColumns(
        modifier = Modifier.fillMaxWidth(),
        narrowColumns = 4,
        wideColumns = 5,
    ) { columns ->
        LazyVerticalGrid(
            columns = GridCells.Fixed(columns),
            contentPadding = PaddingValues(8.dp),
        ) {
            items(12) { Text("Item $it") }
        }
    }
}

@Preview(name = "Wide (>= 700dp)", widthDp = 900, heightDp = 500)
@Composable
private fun WithAdaptiveGridColumnsPreview_Wide() {
    WithAdaptiveGridColumns(
        modifier = Modifier.fillMaxWidth(),
        narrowColumns = 4,
        wideColumns = 5,
    ) { columns ->
        LazyVerticalGrid(
            columns = GridCells.Fixed(columns),
            contentPadding = PaddingValues(8.dp),
        ) {
            items(12) { Text("Item $it") }
        }
    }
}
